import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, ShieldAlert, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { Stepper } from "@/components/ui/stepper";
import { toast } from "@/components/ui/toast";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { useApiQuery } from "@/hooks/use-api";
import { usePermission } from "@/hooks/use-permission";
import {
  describeStepUpOutcome,
  describeStepUpResumeFailure,
  useStepUpResume,
} from "@/hooks/use-step-up-resume";
import { useMobileSigningLocale } from "@/i18n/use-mobile-signing-locale";
import { useStepUpLocale } from "@/i18n/use-step-up-locale";
import {
  createAdminInvite,
  fetchAdminRoles,
  idempotencyKey,
  withStepUpRetry,
  type AdminInviteRequest,
  type AdminRoleDefinition,
  type AdminRolesResult,
  type ResponseSupport,
} from "@/lib/api";

const STEPS = [
  { label: "Profile" },
  { label: "Role & Expiry" },
  { label: "Review & Send" },
];

const EXPIRY_OPTIONS = ["24 hours", "48 hours", "72 hours"];

interface InviteFormData {
  fullName: string;
  email: string;
  role: string;
  inviteExpiry: string;
}

const INITIAL_FORM: InviteFormData = {
  fullName: "",
  email: "",
  role: "",
  inviteExpiry: "48 hours",
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <h4 className="text-sm font-bold text-ink">{title}</h4>
      {children}
    </div>
  );
}

function Label({ children, htmlFor }: { children: React.ReactNode; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} className="mb-1.5 block text-xs font-semibold text-ink">
      {children}
    </label>
  );
}

function InputBox(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className="h-[36px] w-full rounded-[6px] border border-line-strong bg-surface px-3 text-sm text-ink outline-none transition-colors placeholder:text-subtle focus:border-primary focus:shadow-[0_0_0_3px_var(--ads-primary-soft)] disabled:opacity-60"
    />
  );
}

function ReviewRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-line py-2.5 last:border-b-0">
      <span className="text-xs font-semibold text-muted">{label}</span>
      <span className="text-right text-sm text-ink">{value}</span>
    </div>
  );
}

function roleDisplayLabel(role: AdminRoleDefinition): string {
  return role.displayName || role.roleName.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Read state for GET /v1/admin/roles. `errorText` is the message this page has
 * always shown ("HTTP 500", "Network error"); `reasonCode` and `support` keep
 * the server's typed failure metadata so a retry surfaces the latest support
 * reference instead of the previous one. `denied` is a 401/403, which is a
 * terminal state for the page, not a retryable failure.
 */
interface RolesCatalogState {
  roles: AdminRoleDefinition[];
  errorText: string | null;
  reasonCode?: string;
  support: ResponseSupport;
  denied: boolean;
}

const EMPTY_ROLES_CATALOG: RolesCatalogState = {
  roles: [],
  errorText: null,
  support: {},
  denied: false,
};

// Module-level so their identities never change between renders: useApiQuery
// re-runs its effect when the fetcher, mapper or initial data identity moves,
// and a fresh identity per render would refetch in a loop. fetchAdminRoles
// takes no signal; the hook's aborted-controller guard is what discards a
// superseded response when an earlier catalog request is still in flight.
const fetchRolesCatalog = () => fetchAdminRoles();

function mapRolesCatalog(raw: AdminRolesResult): { data: RolesCatalogState } {
  const support: ResponseSupport = {
    supportReference: raw.supportReference,
    capability: raw.capability,
  };
  if (raw.error === "HTTP 401" || raw.error === "HTTP 403") {
    return { data: { roles: [], errorText: null, support, denied: true } };
  }
  if (raw.error) {
    return {
      data: {
        roles: [],
        errorText: raw.error,
        reasonCode: raw.reasonCode,
        support,
        denied: false,
      },
    };
  }
  // A successful read always replaces whatever support metadata the previous
  // failure carried, so a stale reference never outlives its own request.
  return {
    data: {
      roles: raw.roles.filter((r) => r.status === "ACTIVE"),
      errorText: null,
      support: {},
      denied: false,
    },
  };
}

/**
 * Every role option is fed by GET /v1/admin/roles — never a hardcoded list.
 * See ALL-4725 G0/G6: the design mock's static INVITE_ROLES (organization_admin,
 * organization_admin_starter) was a mock-data defect; the live role catalog for
 * this org (agent_operator, finance_viewer, sandbox_designer, sandbox_user,
 * wallet_operator, ...) is what actually exists.
 */
export default function InviteUserPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const canInvite = usePermission("user:invite");
  const catalog = useMobileSigningLocale();
  const stepUp = useStepUpLocale();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<InviteFormData>(INITIAL_FORM);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorSupport, setErrorSupport] = useState<ResponseSupport>({});
  /** Set only by a resume that ran on the way back from a step-up
   * re-authentication (ALL-5021) and could not finish. */
  const [resumeFailure, setResumeFailure] = useState<{ title: string; message: string } & ResponseSupport | null>(null);

  // The write this page parked before sending the browser through a step-up
  // re-authentication is completed and replayed here, on the way back
  // (/auth/login?return_to=/admin/invite). See use-step-up-resume.ts.
  const { resuming } = useStepUpResume({
    actions: ["admin.invite.create"],
    onResuming: () => {
      setResumeFailure(null);
      setError(null);
      setErrorSupport({});
      setSending(true);
    },
    onResumed: (record) => {
      const invited = (record.payload as { email?: string }).email ?? form.email;
      toast({ variant: "success", title: `Invitation sent to ${invited}` });
      navigate("/admin/users");
    },
    onFailed: (failure) => {
      setSending(false);
      const described = describeStepUpResumeFailure(failure, stepUp);
      setResumeFailure({
        title: described.title,
        message: described.message,
        supportReference: described.supportReference,
        capability: described.capability,
      });
    },
  });

  const {
    data: rolesCatalog,
    loading: rolesLoading,
    error: rolesQueryError,
    refresh: refreshRoles,
  } = useApiQuery(fetchRolesCatalog, mapRolesCatalog, {
    initialData: EMPTY_ROLES_CATALOG,
  });
  const roles = rolesCatalog.roles;
  // The mapper carries a read failure the API reported in the catalog state;
  // the hook's own error reports a mapper or fetcher exception the mapper never
  // got to translate. Both are failed reads and must render the retry state,
  // never the successful-empty one (the hook resets data to initialData).
  const rolesError = rolesCatalog.errorText
    ? rolesCatalog.errorText
    : rolesQueryError
      ? // The reason code is shown on its own line below; do not repeat it here.
        "Unable to connect to the admin API."
      : null;
  const rolesErrorReasonCode = rolesCatalog.reasonCode ?? rolesQueryError?.reasonCode;
  /** Support reference (D636) the latest roles request returned with its
   * failure, so the error state names something support can trace. Display only. */
  const rolesErrorSupport: ResponseSupport = rolesCatalog.errorText
    ? rolesCatalog.support
    : {
        supportReference: rolesQueryError?.supportReference,
        capability: rolesQueryError?.capability,
      };
  const rolesDenied = rolesCatalog.denied;
  const rolesDeniedSupport = rolesCatalog.support;

  // Keep the wizard's selection valid against the catalog that actually came
  // back: preserve the chosen role while it is still returned and ACTIVE,
  // otherwise fall back to the first one (the existing initial-selection
  // behaviour). Profile and expiry live in `form` and are untouched.
  useEffect(() => {
    const activeRoles = rolesCatalog.roles;
    if (activeRoles.length === 0) return;
    setForm((f) => {
      if (f.role && activeRoles.some((r) => r.roleName === f.role)) return f;
      return { ...f, role: activeRoles[0].roleName };
    });
  }, [rolesCatalog.roles]);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const canNext =
    step === 0
      ? form.fullName.trim().length > 0 && form.email.trim().length > 0 && emailValid
      : true;

  function handleNext() {
    if (step < 2) setStep(step + 1);
  }

  function handleBack() {
    if (step > 0) setStep(step - 1);
  }

  async function handleSend() {
    setSending(true);
    setError(null);
    setErrorSupport({});
    setResumeFailure(null);
    const expiryHours = form.inviteExpiry === "24 hours" ? 24 : form.inviteExpiry === "72 hours" ? 72 : 48;
    const request: AdminInviteRequest = {
      email: form.email,
      displayName: form.fullName,
      roleName: form.role,
      expiresInHours: expiryHours,
    };
    // One key for both attempts: the first call here and the replay after a
    // step-up re-authentication are the same invite, not two (ALL-5312 requires
    // the header; ALL-5285 requires the replay to reuse it).
    const key = idempotencyKey("admin-invite");
    const outcome = await withStepUpRetry(() => createAdminInvite(request, key), {
      action: "admin.invite.create",
      payload: request,
      idempotencyKey: key,
      returnPath: `${location.pathname}${location.search}`,
    });
    // The browser is on its way to the identity provider; the resume hook sends
    // it back here and replays this invite. Nothing to report in this page.
    if (outcome.redirected) return;
    if (outcome.result.ok) {
      toast({
        variant: "success",
        title: `Invitation sent to ${form.email}`,
        supportReference: outcome.result.supportReference,
      });
      navigate("/admin/users");
      return;
    }
    const described = describeStepUpOutcome(outcome, stepUp);
    setError(described.message ?? "Failed to send invite");
    setErrorSupport({ supportReference: described.supportReference, capability: described.capability });
    setSending(false);
  }

  const roleLabel = roles.find((r) => r.roleName === form.role);
  const roleDisplay = roleLabel ? roleDisplayLabel(roleLabel) : form.role || "—";

  return (
    <div className="mx-auto max-w-[720px]">
      <nav className="mb-5 flex items-center gap-1.5 text-[13px]" aria-label="Breadcrumb">
        <a href="#" onClick={(e) => e.preventDefault()} className="text-primary no-underline hover:underline">Identity</a>
        <span className="text-muted" aria-hidden>›</span>
        <Link to="/admin/users" className="text-primary no-underline hover:underline">Users</Link>
        <span className="text-muted" aria-hidden>›</span>
        <span className="font-medium text-text">Invite</span>
      </nav>

      <div className="mb-6 flex items-center gap-3">
        <div className="grid size-10 place-items-center rounded-lg bg-primary-soft">
          <UserPlus className="size-5 text-primary" />
        </div>
        <div>
          <h1 className="text-xl font-extrabold text-ink">Invite User</h1>
          <p className="text-sm text-muted">Send a secure invite to a new team member.</p>
        </div>
      </div>

      {rolesDenied ? (
        <Card>
          <CardBody>
            <div className="flex items-start gap-3 py-4 text-center">
              <div className="mx-auto flex max-w-[420px] flex-col items-center gap-3">
                <div className="grid size-10 place-items-center rounded-full bg-danger-soft">
                  <ShieldAlert className="size-5 text-danger" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-ink">You don't have access to invite users</h2>
                  <p className="mt-1 text-sm text-muted">
                    Inviting a team member requires an administrator role. Ask an existing admin to send this invite, or
                    request the admin role from your organization owner.
                  </p>
                  <SupportDetails
                    reference={rolesDeniedSupport.supportReference}
                    capability={rolesDeniedSupport.capability}
                    className="items-center"
                  />
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      ) : (
        <>
          <Stepper steps={STEPS} current={step} className="mb-8" />

          <Card>
            <CardBody>
              {step === 0 && (
                <div className="space-y-5">
                  <Section title="Basic Information">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <Label htmlFor="invite-name">Full Name</Label>
                        <InputBox
                          id="invite-name"
                          value={form.fullName}
                          onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
                          placeholder="e.g. Jordan Lee"
                        />
                      </div>
                      <div>
                        <Label htmlFor="invite-email">Email Address</Label>
                        <InputBox
                          id="invite-email"
                          type="email"
                          value={form.email}
                          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                          placeholder="jordan@harbortrust.com"
                        />
                      </div>
                    </div>
                  </Section>
                </div>
              )}

              {step === 1 && (
                <div className="space-y-5">
                  <Section title="Role">
                    {rolesLoading && (
                      <div role="status" className="py-2 text-sm text-muted">Loading roles…</div>
                    )}
                    {!rolesLoading && rolesError && (
                      <div className="rounded-lg border border-danger bg-danger-soft px-4 py-3 text-sm text-danger">
                        Failed to load roles: {rolesError}
                        {rolesErrorReasonCode && (
                          <p className="mt-1 font-mono text-xs">{rolesErrorReasonCode}</p>
                        )}
                        <SupportDetails
                          reference={rolesErrorSupport.supportReference}
                          capability={rolesErrorSupport.capability}
                        />
                        {/* One explicit click issues exactly one GET through the
                            same read adapter; while it is in flight this block
                            is replaced by the loading indicator below. */}
                        <div className="mt-3">
                          <Button variant="secondary" onClick={refreshRoles}>Retry roles</Button>
                        </div>
                      </div>
                    )}
                    {!rolesLoading && !rolesError && roles.length === 0 && (
                      <div className="rounded-lg border border-line bg-bg px-4 py-3 text-sm text-muted">
                        No roles are available to assign right now.
                        {/* A successful empty catalog is also recoverable: the
                            admin can retry the read without leaving the step. */}
                        <div className="mt-3">
                          <Button variant="secondary" onClick={refreshRoles}>Retry roles</Button>
                        </div>
                      </div>
                    )}
                    {!rolesLoading && !rolesError && roles.length > 0 && (
                      <div>
                        <Label htmlFor="invite-role">Assigned Role</Label>
                        <Select
                          id="invite-role"
                          value={form.role}
                          onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                          className="h-[36px] w-full rounded-[6px] border border-line-strong bg-surface px-3 text-sm text-ink"
                        >
                          {roles.map((r) => (
                            <option key={r.roleName} value={r.roleName}>{roleDisplayLabel(r)}</option>
                          ))}
                        </Select>
                        <p className="mt-1 text-xs text-muted">Additional roles can be assigned after the user accepts the invite.</p>
                        {form.role === "transaction_signer" && (
                          <p
                            className="mt-2 rounded-lg border border-line bg-bg px-3 py-2 text-xs leading-relaxed text-muted"
                            data-testid="invite-signer-role-explanation"
                          >
                            {catalog.invites.signerRoleExplanation}
                          </p>
                        )}
                      </div>
                    )}
                    <div className="sm:max-w-[240px]">
                      <Label htmlFor="invite-expiry">Invite Expiry</Label>
                      <Select
                        id="invite-expiry"
                        value={form.inviteExpiry}
                        onChange={(e) => setForm((f) => ({ ...f, inviteExpiry: e.target.value }))}
                        className="h-[36px] w-full rounded-[6px] border border-line-strong bg-surface px-3 text-sm text-ink"
                      >
                        {EXPIRY_OPTIONS.map((o) => (
                          <option key={o} value={o}>{o}</option>
                        ))}
                      </Select>
                    </div>
                  </Section>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-5">
                  <Section title="Review Invitation">
                    <div className="rounded-[8px] border border-line bg-bg px-4">
                      <ReviewRow label="Full Name" value={form.fullName || "—"} />
                      <ReviewRow label="Email" value={form.email || "—"} />
                      <ReviewRow label="Role" value={roleDisplay} />
                      <ReviewRow label="Invite Expiry" value={form.inviteExpiry} />
                    </div>
                  </Section>
                </div>
              )}
            </CardBody>
          </Card>

          {(error || resumeFailure) && (
            <div role="alert" className="mt-3 rounded-lg border border-danger bg-danger-soft px-4 py-2 text-sm text-danger">
              {resumeFailure ? (
                <>
                  <p className="font-semibold">{resumeFailure.title}</p>
                  <p className="mt-0.5">{resumeFailure.message}</p>
                </>
              ) : (
                error
              )}
              <SupportDetails
                reference={resumeFailure ? resumeFailure.supportReference : errorSupport.supportReference}
                capability={resumeFailure ? resumeFailure.capability : errorSupport.capability}
              />
            </div>
          )}

          {resuming && (
            <p role="status" className="mt-3 text-xs text-muted">{stepUp.ui["stepUp.resuming"]}</p>
          )}

          <div className="mt-6 flex items-center justify-between">
            <Button variant="secondary" disabled={step === 0} onClick={handleBack} className="gap-1">
              <ChevronLeft className="size-4" /> Back
            </Button>
            {step < 2 ? (
              <Button disabled={!canNext || (step === 1 && (rolesLoading || !!rolesError || roles.length === 0))} onClick={handleNext} className="gap-1">
                Next <ChevronRight className="size-4" />
              </Button>
            ) : (
              <Button onClick={handleSend} disabled={!canInvite || !form.role} loading={sending}>Send Invite</Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
